import { ref, computed, watch } from 'vue'
import { defineStore } from 'pinia'
import axios from 'axios'
import { voucherReferralService } from '@/api/services/voucherReferral.service'
import type { VoucherReferral, VoucherReferralStatus } from '@/types/voucherReferral.types'

// Domain errors come back as { "detail": "<message>" } (see api-contract §Status
// Codes). FastAPI request-validation 422s use a { "detail": [ ... ] } array
// shape instead. This pulls a human-readable message out of either form.
function extractErrorMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const detail = err.response?.data?.detail
    if (typeof detail === 'string') {
      return detail
    }
    if (Array.isArray(detail) && detail.length > 0) {
      const first = detail[0]
      if (first && typeof first.msg === 'string') {
        return first.msg
      }
    }
  }
  return err instanceof Error ? err.message : 'Unknown error'
}

export const useVoucherReferralStore = defineStore('voucherReferral', () => {
  // State
  const items = ref<VoucherReferral[]>([])
  const loading = ref(false)
  const error = ref<string | null>(null)
  const total = ref(0)
  const page = ref(1)
  const pageSize = ref(20)
  const selectedStatuses = ref<VoucherReferralStatus[]>([])
  const selectedAmbassador = ref<number | null>(null)
  const selectedOrderNumber = ref<string>('')
  const sortBy = ref('id')
  const sortOrder = ref<'asc' | 'desc'>('desc')

  // Getters
  const totalPages = computed(() => Math.ceil(total.value / pageSize.value))
  const hasActiveFilters = computed(
    () =>
      selectedStatuses.value.length > 0 ||
      selectedAmbassador.value !== null ||
      selectedOrderNumber.value.trim() !== '',
  )

  // Actions
  async function updateStatus(itemId: number, newStatus: VoucherReferralStatus) {
    const item = items.value.find((v) => v.id === itemId)
    if (!item) {
      return
    }

    const previousStatus = item.status

    // Optimistic update
    item.status = newStatus

    try {
      await voucherReferralService.updateStatus(itemId, newStatus)
    } catch (err) {
      // Rollback on error
      item.status = previousStatus
      error.value = extractErrorMessage(err)
    }
  }

  async function updateNote(itemId: number, note: string) {
    const item = items.value.find((v) => v.id === itemId)
    if (!item) {
      return
    }

    // Empty / whitespace-only clears the note (stored as NULL server-side).
    const normalized = note.trim() || null
    const previousNote = item.note

    // Optimistic update — the filled/empty icon derives from item.note reactively.
    item.note = normalized

    try {
      await voucherReferralService.updateNote(itemId, normalized)
    } catch (err) {
      // Rollback on error
      item.note = previousNote
      error.value = extractErrorMessage(err)
    }
  }

  // Replace a row in-place after a successful edit (the edit form owns the API
  // call + error handling, so this just syncs the returned record into the list).
  function replaceItem(updated: VoucherReferral) {
    items.value = items.value.map((v) => (v.id === updated.id ? updated : v))
  }

  async function deleteItem(itemId: number) {
    try {
      await voucherReferralService.delete(itemId)
      // Remove from local list and adjust total on success
      items.value = items.value.filter((v) => v.id !== itemId)
      total.value = Math.max(0, total.value - 1)
    } catch (err) {
      error.value = extractErrorMessage(err)
    }
  }

  async function fetchItems() {
    loading.value = true
    error.value = null
    try {
      const response = await voucherReferralService.list({
        page: page.value,
        page_size: pageSize.value,
        sort_by: sortBy.value,
        sort_order: sortOrder.value,
        ...(selectedStatuses.value.length > 0 && { status: selectedStatuses.value }),
        ...(selectedAmbassador.value !== null && { ambassador_id: selectedAmbassador.value }),
        ...(selectedOrderNumber.value.trim() && { order_number: selectedOrderNumber.value.trim() }),
      })
      items.value = response.data.items
      total.value = response.data.total
    } catch (err) {
      error.value = extractErrorMessage(err)
    } finally {
      loading.value = false
    }
  }

  // Paginated fetch for the export screen: page size is set by the export view
  // (300/page) and pinned to the exportable statuses.
  async function fetchExportItems() {
    loading.value = true
    error.value = null
    try {
      const response = await voucherReferralService.list({
        page: page.value,
        page_size: pageSize.value,
        sort_by: sortBy.value,
        sort_order: sortOrder.value,
        status: ['TO_PAY', 'EXPORTED'],
      })
      items.value = response.data.items
      total.value = response.data.total
    } catch (err) {
      error.value = extractErrorMessage(err)
    } finally {
      loading.value = false
    }
  }

  function setSelectedStatuses(statuses: VoucherReferralStatus[]) {
    selectedStatuses.value = statuses
  }

  function setSelectedAmbassador(id: number | null) {
    selectedAmbassador.value = id
  }

  function setSelectedOrderNumber(orderNumber: string) {
    selectedOrderNumber.value = orderNumber
  }

  function resetFilters() {
    selectedStatuses.value = []
    selectedAmbassador.value = null
    selectedOrderNumber.value = ''
    page.value = 1
  }

  function setPage(newPage: number) {
    page.value = newPage
  }

  function clearError() {
    error.value = null
  }

  // Watch filters and refetch when they change
  watch([selectedStatuses, selectedAmbassador, selectedOrderNumber], () => {
    page.value = 1 // Reset to first page when filters change
    fetchItems()
  })

  return {
    items,
    loading,
    error,
    total,
    page,
    pageSize,
    selectedStatuses,
    selectedAmbassador,
    selectedOrderNumber,
    sortBy,
    sortOrder,
    totalPages,
    hasActiveFilters,
    fetchItems,
    fetchExportItems,
    setSelectedStatuses,
    setSelectedAmbassador,
    setSelectedOrderNumber,
    resetFilters,
    setPage,
    clearError,
    updateStatus,
    updateNote,
    replaceItem,
    deleteItem,
  }
})
